import React, { useState, useMemo } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Typography,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Grid,
  Badge,
  Chip,
  IconButton,
  TableSortLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  List,
  ListItem
} from '@mui/material';
import { Close as CloseIcon } from '@mui/icons-material';
import StatusBadge from '../common/StatusBadge';
import { formatDate } from '../../utils/formatters';

/**
 * FeatureChangesTable component for displaying work items in a hierarchical manner based on fix version
 * @param {Object} props - Component props
 * @param {Array} props.workItems - Array of work items to display
 * @param {String} props.fixVersion - The selected fix version
 * @param {Object} props.prsByWorkItem - PRs grouped by work item ID
 * @param {Array} props.pullRequests - All pull requests
 * @param {Function} props.onPRCountClick - Callback when PR count is clicked
 * @returns {JSX.Element} FeatureChangesTable component
 */
const FeatureChangesTable = ({ 
  workItems = [], 
  fixVersion = '', 
  prsByWorkItem = {}, 
  pullRequests = [], 
  onPRCountClick = null,
  workItemTypeFilter = '',
  stateFilter = '',
  showOnlyWithPRs = false,
  showItemsWithoutParent = false
}) => {
  // State for sorting
  const [orderBy, setOrderBy] = useState('');
  const [order, setOrder] = useState('asc');
  
  // State for repository dialog
  const [repoDialogOpen, setRepoDialogOpen] = useState(false);
  const [selectedRepositories, setSelectedRepositories] = useState([]);
  const [selectedWorkItemForRepos, setSelectedWorkItemForRepos] = useState(null);
  
  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Filter and organize work items based on the rules
  const organizeWorkItems = (items, targetFixVersion) => {
    const result = [];
    
    // Group items by their type (exclude bugs - they're shown separately)
    const epics = items.filter(item => item.workItemType === 'Epic');
    const features = items.filter(item => item.workItemType === 'Feature');
    const pbis = items.filter(item => 
      item.workItemType === 'Product Backlog Item' || 
      item.workItemType === 'PBI' ||
      item.workItemType === 'Task'
    );
    
    // Rule 1: If Epic fix version matches, list just Epic
    const matchingEpics = epics.filter(epic => 
      epic.fixVersion === targetFixVersion
    );
    
    // Store matching epic IDs for later use
    const matchingEpicIds = matchingEpics.map(epic => epic.id);
    
    matchingEpics.forEach(epic => {
      result.push({
        ...epic,
        level: 'Epic',
        children: []
      });
    });
    
    // Rule 2: If Feature fix version matches but its epic fix version doesn't match, list just Feature
    const epicIds = matchingEpics.map(epic => epic.id);
    const matchingFeatures = features.filter(feature => 
      feature.fixVersion === targetFixVersion && 
      !epicIds.includes(feature.parent)
    );
    
    matchingFeatures.forEach(feature => {
      result.push({
        ...feature,
        level: 'Feature',
        children: []
      });
    });
    
    // Rule 3: If Epic and Feature don't match, and only PBI fix version matches, list only PBI
    // First, collect all feature IDs (both matching and non-matching)
    const allFeatureIds = features.map(feature => feature.id);
    
    // Get all parent IDs of matching features
    const matchingFeatureParentIds = matchingFeatures.map(feature => feature.parent);
    
    // Get all feature IDs (both matching and non-matching)
    const featureIds = matchingFeatures.map(feature => feature.id);
    
    // Find PBIs that match the fix version AND:
    // 1. Don't have a parent that is a matching feature (to avoid showing PBIs under matching features)
    // 2. Don't have a parent that is a feature whose parent is a matching epic (to avoid showing PBIs under features that are under matching epics)
    const matchingPbis = pbis.filter(pbi => {
      // PBI must match the fix version
      if (pbi.fixVersion !== targetFixVersion) return false;
      
      // If PBI's parent is a matching feature, don't include it
      if (featureIds.includes(pbi.parent)) return false;
      
      // If PBI's parent is a feature whose parent is a matching epic, don't include it
      const parentFeature = features.find(f => f.id === pbi.parent);
      if (parentFeature && matchingEpicIds.includes(parentFeature.parent)) return false;
      
      // Otherwise, include the PBI
      return true;
    });
    
    matchingPbis.forEach(pbi => {
      result.push({
        ...pbi,
        level: 'PBI',
        children: []
      });
    });
    
    return result;
  };
  
  const organizedItems = organizeWorkItems(workItems, fixVersion);
  
  // Get unique work item types and states for filter options
  const uniqueWorkItemTypes = useMemo(() => {
    const types = [...new Set(organizedItems.map(item => item.level))];
    return types.sort();
  }, [organizedItems]);
  
  const uniqueStates = useMemo(() => {
    const states = [...new Set(organizedItems.map(item => item.state))];
    return states.sort();
  }, [organizedItems]);
  
  // Get PR count for a work item
  const getPRCount = (workItemId) => {
    const workItemIdStr = workItemId.toString();
    const directPRs = prsByWorkItem[workItemIdStr] || [];
    
    // Also search in all PRs for mentions of this work item
    const allPRs = pullRequests.filter(pr => {
      const prText = `${pr.title || ''} ${pr.url || ''}`.toLowerCase();
      return prText.includes(`#${workItemIdStr}`) || prText.includes(`ado-${workItemIdStr}`) || prText.includes(`wi${workItemIdStr}`);
    });
    
    // Combine and deduplicate
    const combined = [...directPRs, ...allPRs];
    const unique = combined.filter((pr, index, self) => 
      index === self.findIndex(p => p.number === pr.number && p.repository === pr.repository)
    );
    
    return unique.length;
  };
  
  // Get unique repositories for a work item
  const getRepositories = (workItemId) => {
    const workItemIdStr = workItemId.toString();
    const directPRs = prsByWorkItem[workItemIdStr] || [];
    
    // Also search in all PRs for mentions of this work item
    const allPRs = pullRequests.filter(pr => {
      const prText = `${pr.title || ''} ${pr.url || ''}`.toLowerCase();
      return prText.includes(`#${workItemIdStr}`) || prText.includes(`ado-${workItemIdStr}`) || prText.includes(`wi${workItemIdStr}`);
    });
    
    // Combine and deduplicate PRs
    const combined = [...directPRs, ...allPRs];
    const uniquePRs = combined.filter((pr, index, self) => 
      index === self.findIndex(p => p.number === pr.number && p.repository === pr.repository)
    );
    
    // Extract unique repositories
    const repositories = [...new Set(uniquePRs.map(pr => pr.repository).filter(Boolean))];
    return repositories.sort();
  };
  
  // Handle repository count click
  const handleRepoCountClick = (item) => {
    const repos = getRepositories(item.id);
    setSelectedRepositories(repos);
    setSelectedWorkItemForRepos(item);
    setRepoDialogOpen(true);
  };
  
  // Apply filters to organized items
  const filteredItems = useMemo(() => {
    let filtered = organizedItems.filter(item => {
      // Handle workItemType filter - map "Product Backlog Item" to "PBI" for comparison
      let matchesWorkItemType = true;
      if (workItemTypeFilter) {
        if (workItemTypeFilter === 'Product Backlog Item') {
          // Match both "PBI" level and "Product Backlog Item" workItemType
          matchesWorkItemType = item.level === 'PBI' || item.workItemType === 'Product Backlog Item' || item.workItemType === 'PBI';
        } else {
          matchesWorkItemType = item.level === workItemTypeFilter || item.workItemType === workItemTypeFilter;
        }
      }
      const matchesState = !stateFilter || item.state === stateFilter;
      
      // Check if item has PRs when showOnlyWithPRs is enabled
      let hasPRs = true;
      if (showOnlyWithPRs) {
        const workItemIdStr = item.id.toString();
        const directPRs = prsByWorkItem[workItemIdStr] || [];
        const allPRs = pullRequests.filter(pr => {
          const prText = `${pr.title || ''} ${pr.url || ''}`.toLowerCase();
          return prText.includes(`#${workItemIdStr}`) || prText.includes(`ado-${workItemIdStr}`) || prText.includes(`wi${workItemIdStr}`);
        });
        const combined = [...directPRs, ...allPRs];
        const unique = combined.filter((pr, index, self) => 
          index === self.findIndex(p => p.number === pr.number && p.repository === pr.repository)
        );
        hasPRs = unique.length > 0;
      }
      
      // When toggle is ON, show ONLY items without parent (excluding Epics)
      if (showItemsWithoutParent) {
        const hasNoParent = !item.parent || item.parent === '' || item.parent === null;
        const isNotEpic = item.workItemType !== 'Epic';
        const isItemWithoutParent = hasNoParent && isNotEpic;
        
        // Only show items without parent
        if (!isItemWithoutParent) {
          return false;
        }
      }
      // When toggle is OFF, show all items (no filtering by parent)
      
      return matchesWorkItemType && matchesState && hasPRs;
    });
    
    // Apply sorting
    if (orderBy) {
      filtered = [...filtered].sort((a, b) => {
        let aValue = a[orderBy];
        let bValue = b[orderBy];
        
        // Handle special cases
        if (orderBy === 'id') {
          aValue = parseInt(aValue) || 0;
          bValue = parseInt(bValue) || 0;
        } else if (orderBy === 'createdDate') {
          aValue = aValue ? new Date(aValue).getTime() : 0;
          bValue = bValue ? new Date(bValue).getTime() : 0;
        } else if (orderBy === 'prs') {
          // Get PR count for sorting
          const aIdStr = a.id.toString();
          const bIdStr = b.id.toString();
          const aDirectPRs = prsByWorkItem[aIdStr] || [];
          const bDirectPRs = prsByWorkItem[bIdStr] || [];
          const aAllPRs = pullRequests.filter(pr => {
            const prText = `${pr.title || ''} ${pr.url || ''}`.toLowerCase();
            return prText.includes(`#${aIdStr}`) || prText.includes(`ado-${aIdStr}`) || prText.includes(`wi${aIdStr}`);
          });
          const bAllPRs = pullRequests.filter(pr => {
            const prText = `${pr.title || ''} ${pr.url || ''}`.toLowerCase();
            return prText.includes(`#${bIdStr}`) || prText.includes(`ado-${bIdStr}`) || prText.includes(`wi${bIdStr}`);
          });
          const aCombined = [...aDirectPRs, ...aAllPRs];
          const bCombined = [...bDirectPRs, ...bAllPRs];
          const aUnique = aCombined.filter((pr, index, self) => 
            index === self.findIndex(p => p.number === pr.number && p.repository === pr.repository)
          );
          const bUnique = bCombined.filter((pr, index, self) => 
            index === self.findIndex(p => p.number === pr.number && p.repository === pr.repository)
          );
          aValue = aUnique.length;
          bValue = bUnique.length;
        } else if (orderBy === 'parent') {
          // Handle parent sorting - treat empty/null as empty string for sorting
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        } else {
          // String comparison
          aValue = (aValue || '').toString().toLowerCase();
          bValue = (bValue || '').toString().toLowerCase();
        }
        
        if (aValue < bValue) return order === 'asc' ? -1 : 1;
        if (aValue > bValue) return order === 'asc' ? 1 : -1;
        return 0;
      });
    }
    
    return filtered;
  }, [organizedItems, workItemTypeFilter, stateFilter, showOnlyWithPRs, showItemsWithoutParent, prsByWorkItem, pullRequests, orderBy, order]);
  
  if (organizedItems.length === 0) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        <Typography>No feature changes found for this release.</Typography>
      </Paper>
    );
  }
  
  return (
    <Box>
      {/* Table */}
      <TableContainer component={Paper} sx={{ mb: 4, width: '100%', maxWidth: '100%' }}>
      <Table aria-label="feature changes table" className="feature-changes-table">
        <TableHead>
          <TableRow>
            <TableCell width="80px">
              <TableSortLabel
                active={orderBy === 'id'}
                direction={orderBy === 'id' ? order : 'asc'}
                onClick={() => handleRequestSort('id')}
              >
                ID
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'parent'}
                direction={orderBy === 'parent' ? order : 'asc'}
                onClick={() => handleRequestSort('parent')}
              >
                Parent
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'level'}
                direction={orderBy === 'level' ? order : 'asc'}
                onClick={() => handleRequestSort('level')}
              >
                Level
              </TableSortLabel>
            </TableCell>
            <TableCell width="30%">
              <TableSortLabel
                active={orderBy === 'title'}
                direction={orderBy === 'title' ? order : 'asc'}
                onClick={() => handleRequestSort('title')}
              >
                Title
              </TableSortLabel>
            </TableCell>
            <TableCell width="90px">
              <TableSortLabel
                active={orderBy === 'state'}
                direction={orderBy === 'state' ? order : 'asc'}
                onClick={() => handleRequestSort('state')}
              >
                State
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'fixVersion'}
                direction={orderBy === 'fixVersion' ? order : 'asc'}
                onClick={() => handleRequestSort('fixVersion')}
              >
                Fix Version
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'createdDate'}
                direction={orderBy === 'createdDate' ? order : 'asc'}
                onClick={() => handleRequestSort('createdDate')}
              >
                Created Date
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              <TableSortLabel
                active={orderBy === 'prs'}
                direction={orderBy === 'prs' ? order : 'asc'}
                onClick={() => handleRequestSort('prs')}
              >
                PRs
              </TableSortLabel>
            </TableCell>
            <TableCell width="100px">
              Repos
            </TableCell>
            <TableCell width="150px" sx={{ 
              maxWidth: '150px',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap'
            }}>
              <TableSortLabel
                active={orderBy === 'team'}
                direction={orderBy === 'team' ? order : 'asc'}
                onClick={() => handleRequestSort('team')}
              >
                Team
              </TableSortLabel>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {filteredItems.map((item) => {
            const prCount = getPRCount(item.id);
            // Check if item doesn't have a parent (excluding Epics)
            const hasNoParent = !item.parent || item.parent === '' || item.parent === null;
            const isNotEpic = item.workItemType !== 'Epic';
            const shouldHighlight = hasNoParent && isNotEpic;
            
            return (
              <TableRow 
                key={item.id}
                sx={{
                  backgroundColor: shouldHighlight ? 'rgba(158, 158, 158, 0.2)' : 'inherit',
                  '&:hover': {
                    backgroundColor: shouldHighlight ? 'rgba(158, 158, 158, 0.3)' : 'rgba(0, 0, 0, 0.04)'
                  }
                }}
              >
                <TableCell>
                  <Link 
                    href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${item.id}`} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    sx={{ color: '#1976d2', textDecoration: 'none' }}
                  >
                    {item.id}
                  </Link>
                </TableCell>
                <TableCell>
                  {item.parent ? (
                    <Link 
                      href={`https://tr-legal.visualstudio.com/HighQ%20Tech/_workitems/edit/${item.parent}`} 
                      target="_blank" 
                      rel="noopener noreferrer"
                      sx={{ color: '#1976d2', textDecoration: 'none' }}
                    >
                      {item.parent}
                    </Link>
                  ) : (
                    <Typography variant="body2" color="textSecondary">-</Typography>
                  )}
                </TableCell>
                <TableCell>{item.level}</TableCell>
                <TableCell>{item.title}</TableCell>
                <TableCell>
                  <StatusBadge status={item.state} />
                </TableCell>
                <TableCell>{item.fixVersion || '-'}</TableCell>
                <TableCell>{formatDate(item.createdDate)}</TableCell>
                <TableCell>
                  {prCount > 0 ? (
                    <Chip
                      label={prCount}
                      size="small"
                      color="primary"
                      onClick={() => onPRCountClick && onPRCountClick(item)}
                      sx={{ cursor: onPRCountClick ? 'pointer' : 'default' }}
                    />
                  ) : (
                    '-'
                  )}
                </TableCell>
                <TableCell>
                  {(() => {
                    const repos = getRepositories(item.id);
                    const repoCount = repos.length;
                    return repoCount > 0 ? (
                      <Chip
                        label={repoCount}
                        size="small"
                        color="secondary"
                        onClick={() => handleRepoCountClick(item)}
                        sx={{ cursor: 'pointer' }}
                      />
                    ) : (
                      '-'
                    );
                  })()}
                </TableCell>
                <TableCell sx={{ 
                  maxWidth: '150px',
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap'
                }}>{item.team || '-'}</TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      </TableContainer>
      
      {/* Repository Dialog */}
      <Dialog 
        open={repoDialogOpen} 
        onClose={() => setRepoDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Repositories for {selectedWorkItemForRepos?.title || `Work Item ${selectedWorkItemForRepos?.id}`}
            </Typography>
            <IconButton onClick={() => setRepoDialogOpen(false)} size="small">
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          {selectedRepositories.length > 0 ? (
            <List>
              {selectedRepositories.map((repo, index) => (
                <ListItem key={index} sx={{ px: 0 }}>
                  <Typography variant="body2">{repo}</Typography>
                </ListItem>
              ))}
            </List>
          ) : (
            <Typography variant="body2" color="textSecondary">
              No repositories found for this work item.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRepoDialogOpen(false)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default FeatureChangesTable;
